// Procedural GPU effects (shield rings, circuit arcs, bullets).

import { Container, Graphics } from 'pixi.js';
import type { Camera } from '../camera';
import { G } from '../../core/state';
import { SCREEN_CX, SCREEN_CY, S, RES_SCALE, TILE } from '../../core/constants';
import { physPal, consumePalDirty } from '../palette';
import { rand as rnd } from '../../core/rng';
import { worldToScreen, TILE_DRAW_MARGIN } from '../camera';
import { profilerMeasure } from '../../dev/profiler';

const M_PI = Math.PI;
const tr = Math.trunc;

const layer = new Container();
let gfx = new Graphics();
const bgLayer = new Container();
let bgGfx = new Graphics();
let shieldT = 0;
let palRevision = 0;

export function getFxLayer(): Container {
  return layer;
}

export function getBgLayer(): Container {
  if (!bgGfx.parent) bgLayer.addChild(bgGfx);
  return bgLayer;
}

function palRgb(idx: number): number {
  const v = (idx & 255) * 3;
  return (physPal[v] << 16) | (physPal[v + 1] << 8) | physPal[v + 2];
}

function ensureGfx(): void {
  if (!gfx.parent) {
    gfx = new Graphics();
    layer.addChild(gfx);
  }
}

export function clearFxGpu(): void {
  gfx.clear();
}

export function drawFilledRectGpu(x: number, y: number, w: number, h: number, c: number): void {
  ensureGfx();
  gfx.rect(x, y, w, h);
  gfx.fill(palRgb(c));
}

export function drawCircleGpu(x: number, y: number, r: number, c: number): void {
  ensureGfx();
  x = tr(x);
  y = tr(y);
  r = tr(r);
  if (r < 1) return;
  const color = palRgb(c);
  for (let circ_y = 0; circ_y < r; circ_y++) {
    if (circ_y < r - 10) {
      const outer = tr(Math.sqrt(r * r - circ_y * circ_y));
      const inner = tr(Math.sqrt((r - 10) * (r - 10) - circ_y * circ_y));
      gfx.rect(x - outer, y - circ_y, outer - inner, 1);
      gfx.fill(color);
      gfx.rect(x + inner, y - circ_y, outer - inner, 1);
      gfx.fill(color);
      gfx.rect(x - outer, y + circ_y, outer - inner, 1);
      gfx.fill(color);
      gfx.rect(x + inner, y + circ_y, outer - inner, 1);
      gfx.fill(color);
    } else {
      const len = tr(Math.sqrt(r * r - circ_y * circ_y));
      gfx.rect(x - len, y - circ_y, len * 2, 1);
      gfx.fill(color);
      gfx.rect(x - len, y + circ_y, len * 2, 1);
      gfx.fill(color);
    }
  }
}

export function drawCircleExGpu(x: number, y: number, r: number, r2: number, c: number): void {
  ensureGfx();
  x = tr(x);
  y = tr(y);
  r = tr(r);
  r2 = tr(r2);
  const diffi = r - r2;
  if (r < 1) return;
  const color = palRgb(c);
  for (let circ_y = 0; circ_y < r; circ_y++) {
    if (circ_y < r - diffi) {
      const outer = tr(Math.sqrt(r * r - circ_y * circ_y));
      const inner = tr(Math.sqrt((r - diffi) * (r - diffi) - circ_y * circ_y));
      gfx.rect(x - outer, y - circ_y, outer - inner, 1);
      gfx.fill(color);
      gfx.rect(x + inner, y - circ_y, outer - inner, 1);
      gfx.fill(color);
      gfx.rect(x - outer, y + circ_y, outer - inner, 1);
      gfx.fill(color);
      gfx.rect(x + inner, y + circ_y, outer - inner, 1);
      gfx.fill(color);
    } else {
      const len = tr(Math.sqrt(r * r - circ_y * circ_y));
      gfx.rect(x - len, y - circ_y, len * 2, 1);
      gfx.fill(color);
      gfx.rect(x - len, y + circ_y, len * 2, 1);
      gfx.fill(color);
    }
  }
}

function thinLineGpu(x1: number, y1: number, x2: number, y2: number, col: number): void {
  ensureGfx();
  gfx.moveTo(x1, y1);
  gfx.lineTo(x2, y2);
  gfx.stroke({ width: 1, color: palRgb(col), cap: 'round' });
}

export function drawArcGpu(x: number, y: number, r: number, dir: number): void {
  let bright = (rnd() % 128) + 63;
  let i = 0;
  let l_x = x;
  let l_y = y;
  while (i < r) {
    i += (rnd() % 5) + 25;
    const pdir = dir + ((rnd() % 16) / 16.0) * 2.0 * (M_PI / 15.0);
    const ndir = dir - ((rnd() % 16) / 16.0) * 2.0 * (M_PI / 15.0);
    const cdir =
      dir +
      ((rnd() % 16) / 16.0) * 2.0 * (M_PI / 20.0) -
      ((rnd() % 16) / 16.0) * 2.0 * (M_PI / 20.0);

    bright += rnd() % 30;
    bright -= rnd() % 30;
    if (bright < 0) bright = 0;
    if (bright > 255) bright = 255;

    const c1x = x + Math.cos(pdir) * i;
    const c1y = y + Math.sin(pdir) * i;
    thinLineGpu(l_x, l_y, c1x, c1y, bright);
    const c2x = x + Math.cos(ndir) * i;
    const c2y = y + Math.sin(ndir) * i;
    thinLineGpu(l_x, l_y, c2x, c2y, bright);

    for (let c = 0; c < 5; c++) {
      const px = tr(
        x + Math.cos(dir - M_PI / 10.0 + ((rnd() % 16) / 16.0) * 2.0 * (M_PI / 10.0)) * i
      );
      const py = tr(
        y + Math.sin(dir - M_PI / 10.0 + ((rnd() % 16) / 16.0) * 2.0 * (M_PI / 10.0)) * i
      );
      drawFilledRectGpu(px, py, 1, 1, (rnd() % 128) + 63);
    }

    i += (rnd() % 5) + 25;
    const cx = x + Math.cos(cdir) * i;
    const cy = y + Math.sin(cdir) * i;
    thinLineGpu(c1x, c1y, cx, cy, bright);
    thinLineGpu(c2x, c2y, cx, cy, bright);
    l_x = cx;
    l_y = cy;
  }
}

export function drawShieldGpu(cam: Camera): void {
  profilerMeasure('gpu.shield', () => {
    shieldT++;
    if (G.player_shield === 0 || G.shield_hp === 0) return;
    let s_size = G.shield_hp;
    let belts = 0;
    if (s_size > 15) {
      belts = s_size - 15;
      s_size = 15;
    }
    const cx = Math.round(cam.viewW / 2);
    const cy = Math.round(cam.viewH / 2);
    drawCircleExGpu(
      cx,
      cy,
      S(28 + s_size),
      S(28 - s_size),
      128 +
        tr((G.shield_hp * 127) / G.player_shield) -
        (50 * (G.shield_hp < G.player_shield ? 1 : 0) + G.shield_recover) -
        45 +
        (shieldT % 4) * 15
    );
    for (let i = 0; i < belts; i++) {
      const bpos = S(13) + tr((S(30) * (i + 1)) / (belts + 1));
      drawCircleExGpu(cx, cy, bpos + S(1), bpos - S(1), ((i + shieldT) % 6) * 12);
    }
  });
}

export function drawCircuitArcsGpu(cam: Camera, startDir: number, nArcs: number): void {
  let arcdir = startDir;
  const px = Math.round(cam.viewW / 2);
  const py = Math.round(cam.viewH / 2);
  for (let i = 0; i < nArcs; i++) {
    drawArcGpu(px, py, G.circuit_range, arcdir);
    arcdir += ((rnd() % 16) / 16.0) * ((M_PI * 2) / nArcs);
  }
}

export function drawCircuitBarGpu(): void {
  if (!G.magic_circuit) return;
  const vd = S(520);
  const a = Math.abs(G.magic_circuit);
  const pos = G.magic_circuit > 0;
  const w = tr((a * vd) / G.circuit_size);
  const colors = pos ? [159, 183, 207, 231, 255] : [72, 80, 96, 112, 128];
  const rects = [
    [S(110), S(469), S(8) + w, S(9)],
    [S(111), S(470), S(6) + w, S(7)],
    [S(112), S(471), S(4) + w, S(5)],
    [S(113), S(472), S(2) + w, S(3)],
    [S(114), S(473), w, S(1)],
  ];
  for (let i = 0; i < 5; i++) {
    const [x, y, rw, rh] = rects[i];
    drawFilledRectGpu(x, y, rw, rh, colors[i]);
  }
}

export function drawWorldCircleGpu(wx: number, wy: number, r: number, c: number, cam: Camera): void {
  const s = worldToScreen(wx, wy, cam);
  drawCircleGpu(s.x, s.y, r, c);
}

export function syncFxPalette(): void {
  if (consumePalDirty()) palRevision++;
}

/** Background fill for dungeon view (palette index 255) — drawn behind tilemap/sprites. */
export function clearBgLayerGpu(cam: Camera): void {
  if (!bgGfx.parent) bgLayer.addChild(bgGfx);
  bgGfx.clear();
  const pad = TILE_DRAW_MARGIN * TILE;
  bgGfx.rect(-pad, -pad, cam.viewW + pad * 2, cam.viewH + pad * 2);
  bgGfx.fill(palRgb(255));
}

/** @deprecated Use clearBgLayerGpu — kept for callers that only fill background. */
export function drawViewBackgroundGpu(cam: Camera): void {
  clearBgLayerGpu(cam);
}
